import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { InformeData } from '../types';
import { images } from '../utils/imageBase64';

// Variables globales para tamaños de fuente (mismo que el informe original)
const FONT_SIZES = {
  base: 12,
  h1: 16,
  h2: 14,
  h3: 13,
  h4: 12,
  paragraph: 11.5,
  paragraphCenter: 12,
  list: 13,
  field: 10.5,
  headerTitle: 12.5,
  brand: 14.5,
  footer: 9.5,
  bibliography: 10,
  link: 10,
  sectionTitle: 11.5,
  tableHeader: 8.5,
  tableCell: 8,
  tableCellContent: 10,
  tableTitle: 10.5,
  importantTitle: 11.5,
  importantText: 10.5,
};

// Estilos para el PDF (mismo que el informe original)
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 50,
    paddingTop: 40,
    paddingBottom: 60,
    fontFamily: 'Helvetica',
    fontSize: FONT_SIZES.base,
  },
  header: {
    flexDirection: 'row',
    marginBottom: 6,
    alignItems: 'flex-start',
  },
  logo: {
    height: 70,
    width: 'auto',
    marginRight: 12,
    objectFit: 'contain',
  },
  headerTitle: {
    flex: 1,
    fontSize: FONT_SIZES.headerTitle,
    lineHeight: 1.4,
  },
  brand: {
    fontSize: FONT_SIZES.brand,
    fontWeight: 'bold',
    marginBottom: 1,
  },
  content: {
    flex: 1,
  },
  h1: {
    fontSize: FONT_SIZES.h1,
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: 5,
    color: '#000',
  },
  h2: {
    fontSize: FONT_SIZES.h2,
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: 5,
    color: '#000',
  },
  h3: {
    fontSize: FONT_SIZES.h3,
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: 4,
    color: '#c00',
  },
  h4: {
    fontSize: FONT_SIZES.h4,
    fontWeight: 'bold',
    marginTop: 6,
    marginBottom: 4,
    color: '#000',
  },
  paragraph: {
    fontSize: FONT_SIZES.paragraph,
    lineHeight: 1.4,
    marginBottom: 8,
    textAlign: 'justify',
    color: '#000',
  },
  paragraphCenter: {
    fontSize: FONT_SIZES.paragraphCenter,
    lineHeight: 1.4,
    marginBottom: 10,
    marginTop: 8,
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#c00',
  },
  list: {
    marginVertical: 4,
    paddingLeft: 8,
  },
  listItem: {
    fontSize: FONT_SIZES.list,
    lineHeight: 1.45,
    marginBottom: 4,
  },
  strong: {
    fontWeight: 'bold',
  },
  field: {
    backgroundColor: 'transparent',
    padding: 6,
    marginVertical: 4,
    fontSize: FONT_SIZES.field,
    lineHeight: 1.4,
    minHeight: 25,
  },
  table: {
    width: '100%',
    marginVertical: 4,
    marginTop: 8,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCell: {
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    borderBottomStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#333',
    borderRightStyle: 'solid',
    padding: 1.5,
    fontSize: 6.5,
    lineHeight: 2.4,
    minHeight: 16,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  tableCellFirst: {
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    borderBottomStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#333',
    borderRightStyle: 'solid',
    borderLeftWidth: 1,
    borderLeftColor: '#333',
    borderLeftStyle: 'solid',
    padding: 1.5,
    fontSize: 6.5,
    lineHeight: 2.4,
    minHeight: 16,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  tableCellHeader: {
    borderTopWidth: 1,
    borderTopColor: '#333',
    borderTopStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    borderBottomStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#333',
    borderRightStyle: 'solid',
    padding: 2,
    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',
    lineHeight: 1.1,
    minHeight: 20,
  },
  tableCellHeaderFirst: {
    borderTopWidth: 1,
    borderTopColor: '#333',
    borderTopStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    borderBottomStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#333',
    borderRightStyle: 'solid',
    borderLeftWidth: 1,
    borderLeftColor: '#333',
    borderLeftStyle: 'solid',
    padding: 2,
    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',
    lineHeight: 1.1,
    minHeight: 20,
  },
  greenBg: {
    backgroundColor: '#cfe8cf',
  },
  lightGreenBg: {
    backgroundColor: '#e8efdd',
  },
  pinkBg: {
    backgroundColor: '#f4c6cc',
  },
  image: {
    width: 140,
    height: 'auto',
    marginBottom: 6,
    marginLeft: 8,
  },
  imageWide: {
    width: 220,
    height: 'auto',
    marginVertical: 8,
    alignSelf: 'center',
  },
  imageContainer: {
    marginVertical: 4,
    alignItems: 'center',
  },
  imageRight: {
    width: 140,
    height: 'auto',
    marginBottom: 6,
    marginLeft: 8,
  },
  quoteBox: {
    backgroundColor: '#f7eaea',
    borderLeftWidth: 3,
    borderLeftColor: '#c00',
    borderLeftStyle: 'solid',
    padding: 8,
    marginVertical: 6,
    marginHorizontal: 8,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 50,
    right: 50,
    fontSize: FONT_SIZES.footer,
    fontWeight: 'bold',
    color: '#666',
    textAlign: 'left',
  }
});

interface InformeSeguimientoPDFProps {
  data: InformeData;
}

const InformeSeguimientoPDF: React.FC<InformeSeguimientoPDFProps> = ({ data }) => {
  const renderHeader = (content?: React.ReactNode, onlyLogo: boolean = false) => (
    <View style={styles.header}>
      <Image style={styles.logo} src={images.logo} />
      {!onlyLogo && (
        <View style={styles.headerTitle}>
          {content || (
            <>
              <Text>
                <Text style={styles.brand}>CIP</Text>
                <Text> es una entidad especializada en ofrecer servicios integrales de cuidado de personas, destacándose por su enfoque personalizado.</Text>
              </Text>
            </>
          )}
        </View>
      )}
    </View>
  );

  const renderFooter = () => (
    <Text style={styles.footer} fixed>
      Medicina preventiva, personalizada y longevidad.
    </Text>
  );

  const getFieldValue = (key: string) => data.campos[key] || '';

  return (
    <Document>
      {/* ========= PÁGINA 1 - INTRODUCCIÓN RECORTADA ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            {data.paciente.nombre && (
              <Text style={{fontSize: FONT_SIZES.h4, fontWeight: 'bold', marginBottom: 4}}>
                {data.paciente.nombre}
              </Text>
            )}
            <Text>
              <Text style={styles.brand}>CIP </Text>es una entidad especializada en ofrecer servicios integrales de cuidado de personas, destacándose por su enfoque personalizado.
            </Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.paragraph}>
            A través de su unidad <Text style={{fontStyle: 'italic'}}>Cuidado Integral de Personas</Text>, se centra en proporcionar <Text style={{fontWeight: 'bold'}}>calidad de vida</Text> tanto para personas que requieren cuidados especiales con enfermedades ya de base (cáncer, enf. inmunológicas, metabólicas) como para aquellos que buscan mejorar su estilo de vida y longevidad.
          </Text>

          <Text style={styles.paragraphCenter}>
            Lo que ofrecemos es un cambio de estilo de vida.
          </Text>

          <Text style={styles.paragraph}>
            La medicina y el estilo de vida están profundamente interconectados, ya que las decisiones cotidianas que tomamos sobre nuestra salud y bienestar emocional pueden tener un impacto significativo en nuestra calidad de vida y en la prevención de enfermedades y longevidad.
          </Text>

          <Text style={styles.paragraph}>
            Un estilo de vida saludable implica tomar decisiones conscientes sobre <Text style={{fontWeight: 'bold'}}>alimentación</Text>, <Text style={{fontWeight: 'bold'}}>ejercicio</Text>, <Text style={{fontWeight: 'bold'}}>descanso</Text>, <Text style={{fontWeight: 'bold'}}>manejo del estrés</Text> y hábitos sociales "tribu".
          </Text>

          <Text style={styles.h3}>¿Por qué es tan importante el estilo de vida? ¿Cómo nos influye?</Text>

          {/* Sección con imagen a la derecha y texto a la izquierda */}
          <View style={{flexDirection: 'row', marginVertical: 4}}>
            <View style={{flex: 1, paddingRight: 8}}>
            <Text style={styles.paragraph}>
              La respuesta es la <Text style={{fontWeight: 'bold'}}>epigenética</Text>, un campo fascinante de la biología que estudia los cambios en la expresión genética que no implican alteraciones en la secuencia del ADN, pero que pueden influir en cómo se activan o desactivan los genes.
            </Text>

              <Text style={styles.paragraph}>
                Estos cambios epigenéticos son clave para entender cómo factores ambientales —como la alimentación, el estrés, el ejercicio y otros aspectos del estilo de vida— pueden afectar la salud a lo largo de toda la vida y, en algunos casos, incluso ser transmitidos a las siguientes generaciones.
              </Text>
            </View>
            <Image style={styles.imageRight} src={images.imagen_1} />
          </View>

            <Text style={styles.paragraph}>
              En rasgos generales podemos hablar de un <Text style={{fontWeight: 'bold'}}>20% genética</Text> y un <Text style={{fontWeight: 'bold'}}>80% epigenética</Text>: la genética establece la predisposición y la epigenética determina el grado en que esa predisposición se manifiesta.
            </Text>

          <Text style={styles.paragraph}>
            Los factores ambientales, incluidos la dieta, la actividad física, la exposición a toxinas (como el humo del tabaco), el estrés y la falta de sueño, pueden modificar la forma en que se expresan nuestros genes, incluso si no alteran la secuencia genética subyacente. Esto significa que nuestro estilo de vida puede influir en nuestra biología a un nivel muy profundo.
          </Text>
        </View>
      </Page>

      {/* ========= PÁGINA 2 ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={styles.paragraph}>
              Muchas formas de cáncer están relacionadas con cambios epigenéticos. Por ejemplo, ciertos genes supresores de tumores pueden ser "apagados" por modificaciones epigenéticas, mientras que otros genes que promueven el crecimiento tumoral pueden ser "activados".
            </Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.paragraph}>
            Enfermedades como la depresión, la esquizofrenia y el trastorno bipolar se han relacionado con alteraciones epigenéticas, especialmente en cómo los genes responden a factores estresantes y a los cambios en el ambiente. Las modificaciones epigenéticas pueden influir en el metabolismo y en la inflamación, contribuyendo al desarrollo de condiciones como la diabetes tipo 2, la hipertensión y la aterosclerosis; así como no olvidar el sistema inmunológico, que responde a los propios tejidos del cuerpo, lo que puede contribuir al desarrollo de enfermedades autoinmunes.
          </Text>

          <View style={styles.imageContainer}>
            <Image style={styles.imageWide} src={images.imagen_2} />
          </View>

          <Text style={styles.paragraphCenter}>
            La epigenética subraya la increíble capacidad que tenemos para "modificar nuestra biología a través de nuestras elecciones diarias".
          </Text>

          <Text style={styles.paragraph}>
            Un enfoque multidisciplinar permite abordar enfermedades complejas que requieren la intervención de diferentes especialistas para obtener una solución completa, aunando la medicina convencional con estrategias complementarias.
          </Text>

          <Text style={styles.paragraph}>
            CIP está formado por un conjunto de especialistas sanitarios: médicos especialistas en oncología médica, medicina interna y medicina de familia, fisioterapeutas, psicología y terapia, preparadores físicos, PNI y podólogos, entre otros. Esta visión integradora nos permite diseñar planes de cuidado individualizados y basados en la evidencia.
          </Text>

          <Text style={styles.paragraph}>
            Buscamos tratar al paciente de manera integral, considerando no solo los síntomas físicos, sino también sus necesidades emocionales, sociales y psicológicas. De esta forma, el plan de intervención acompaña al paciente y a su entorno, promoviendo hábitos sostenibles que impacten positivamente en su salud y calidad de vida.
          </Text>

          <Text style={styles.paragraph}>
            Cuando un paciente ingiere varios medicamentos, nuestro personal médico analiza con un software especializado las posibles interacciones entre dichos medicamentos u otras sustancias de consumo habitual —como pueden ser infusiones, especias o alimentos—. El objetivo de este estudio es prevenir efectos secundarios indeseados y optimizar la función de los medicamentos para que sean tan eficaces y seguros como sea posible.
          </Text>
        </View>
      </Page>

      {/* ========= PÁGINA 3 ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={{...styles.paragraph, fontWeight: 'bold'}}>
              Tus hábitos de vida te han llevado a estar en la situación actual, por lo que un cambio de estos hábitos será la ruta a la mejoría.
            </Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.paragraph}>
            Este cambio de estilo de vida puede conllevar también un cambio de hábito alimenticio. Una dieta implica un día de inicio y un día de fin. ¿Quieres estar saludable durante unos meses, o prefieres estar saludable toda la vida?
          </Text>

          <View style={styles.quoteBox}>
            <Text style={{fontWeight: 'bold', marginBottom: 2, textAlign: 'center'}}>
              No podemos pretender que las cosas cambien si seguimos haciendo lo mismo.
            </Text>
            <Text style={{fontWeight: 'normal', textAlign: 'center'}}>
              (Albert Einstein, físico)
            </Text>
          </View>

          <Text style={styles.paragraph}>
            Por estos motivos CIP te recomendamos encarecidamente que tengas una alimentación extremadamente variada y no pases por alto nuestras recomendaciones.
          </Text>

          <Text style={styles.paragraph}>
            Es por eso que los alimentos catalogados como <Text style={{fontWeight: 'bold'}}>no recomendados</Text> son alimentos que deberías evitar no sólo mientras te encuentres en fase de recuperación, sino como medida preventiva. De la misma manera los alimentos que recomendamos consumir frecuentemente deberías de incorporarlos a tu alimentación incluso una vez superada la fase de recuperación, ya que igual de perjudicial es alimentarse con algo potencialmente tóxico o dañino, que dejar de recibir nutrientes o micronutrientes. Las dos situaciones generan enfermedad. La clave de la salud es, entre otros motivos, consumir cuanta más diversidad de alimentos saludables mejor.
          </Text>

          <Text style={{...styles.paragraph, color: '#c00', fontWeight: 'bold', marginTop: 6}}>
            ESTRATEGIAS DE TRATAMIENTOS BASADAS EN EVIDENCIA CIENTÍFICA:
          </Text>

          <Text style={styles.paragraph}>
            Las estrategias que te recomendamos para tu tratamiento te pueden parecer exageradas y que van en contra de nuestra costumbre de comer, pero su utilidad terapéutica está más que demostrada científicamente. Su enfoque no es restablecer un ritmo fisiológico, generar un cambio metabólico e inmunológico y estimular a un área del cerebro que se denomina hipotálamo, que se encarga del grado de mantener estables casi todos los parámetros importantes para mantener una salud óptima.
          </Text>

          <Text style={styles.paragraph}>
            Esto lo conseguimos con diferentes estrategias: alimentación, medicación, actividad física, hábitos de vida, ejercicios respiratorios, ejercicios de temperatura… Posiblemente te recomendemos cambios que te resultarán "raros" para todavía la mayoría de la sociedad de hoy en día, pero todos ellos estarán justificados científicamente. <Text style={{fontWeight: 'bold'}}>CONFÍA.</Text>
          </Text>

          <Text style={{...styles.paragraph, textAlign: 'center', marginTop: 6}}>
            Te la detallamos a continuación.
          </Text>
        </View>
      </Page>

      {/* ========= PÁGINA 4 - RECOMENDACIONES A SEGUIR ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={styles.h3}>RECOMENDACIONES A SEGUIR:</Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <View style={styles.field}>
            {getFieldValue('recomendaciones_seguir') ? (
              <Text>{getFieldValue('recomendaciones_seguir')}</Text>
            ) : (
              <Text style={{color: '#999'}}>Recomendaciones a seguir...</Text>
            )}
          </View>
        </View>
      </Page>

      {/* ========= PÁGINA 5 - SUPLEMENTACIÓN ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={styles.h3}>SUPLEMENTACIÓN:</Text>
            <Text style={styles.paragraph}>
              Los suplementos que te indicamos a continuación son productos avalados científicamente, no interaccionan con ningún tipo de fármaco que estés tomando. Igualmente no generan adicción, pretenden restablecer algunos nutrientes, ayudar a mitigar los efectos secundarios de los tratamientos farmacológicos sin restarle eficacia, aumentar la modulación de nuestro sistema inmune o simplemente intentar o ayudar a frenar directamente tu patología. Los puedes encontrar o encargar en cualquier parafarmacia y herbolario especializado.
            </Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          {getFieldValue('suplementacion') && (
            <View style={styles.field}>
              <Text>{getFieldValue('suplementacion')}</Text>
            </View>
          )}
        </View>
      </Page>

      {/* ========= PÁGINA 6 - MEDICACIÓN ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={styles.h3}>MEDICACIÓN:</Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <View style={styles.field}>
            {getFieldValue('medicacion') ? (
              <Text>{getFieldValue('medicacion')}</Text>
            ) : (
              <Text style={{color: '#999'}}>Medicación...</Text>
            )}
          </View>

          <View style={{
            marginTop: 10,
            padding: 10,
            borderWidth: 1,
            borderColor: '#e0c5a5',
            borderStyle: 'solid',
            backgroundColor: '#fff6ee',
            borderRadius: 8,
            width: '80%',
            alignSelf: 'center',
          }}>
            <Text style={{fontSize: FONT_SIZES.importantTitle, fontWeight: 'bold', color: '#c00', textAlign: 'center', marginBottom: 4}}>
              IMPORTANTE
            </Text>
            <Text style={{fontSize: FONT_SIZES.importantText, color: '#c00', textAlign: 'center', lineHeight: 1.4}}>
              Es muy frecuente que, cuando se comienza a establecer nuevos hábitos de vida, aparezcan durante unos días algunos síntomas como cefaleas, cansancio o dolores. No te alarmes. Esto no debe constituir un motivo para abandonar nuestras recomendaciones. Estos síntomas no suelen persistir más de 7–10 días.
            </Text>
          </View>
        </View>
      </Page>

    </Document>
  );
};

export default InformeSeguimientoPDF;


